'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getInstagramTiles } from './instagram.functions';
import { usePixelatedConfig } from "../config/config.client";
import { Tiles } from '../elements/tiles';
/**
 * InstagramTiles — Fetch Instagram media and render as tiles.
 *
 * @param {number} [props.limit] - Maximum number of media items to fetch (default: 12).
 * @param {number} [props.rowCount] - Number of columns/rows to use in the tiles layout.
 * @param {boolean} [props.useThumbnails] - Whether to prefer thumbnail images over full-size media.
 * @param {boolean} [props.includeVideos] - Include video posts in results when true.
 * @param {boolean} [props.includeCaptions] - Include captions in the returned tile metadata.
 */
InstagramTiles.propTypes = {
    /** Max number of media items to fetch */
    limit: PropTypes.number,
    /** Number of columns/rows used by Tiles layout */
    rowCount: PropTypes.number,
    /** Prefer thumbnails when available */
    useThumbnails: PropTypes.bool,
    /** Include video posts */
    includeVideos: PropTypes.bool,
    /** Include captions in tile metadata */
    includeCaptions: PropTypes.bool,
};
export function InstagramTiles(props) {
    const config = usePixelatedConfig();
    const [tiles, setTiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const accessToken = config?.integrations?.instagram?.accessToken;
    const userId = config?.integrations?.instagram?.userId;
    useEffect(() => {
        (async () => {
            try {
                const result = await getInstagramTiles({
                    accessToken: accessToken ?? undefined,
                    userId: userId ?? undefined,
                    limit: props.limit ?? 12,
                    useThumbnails: props.useThumbnails ?? undefined,
                    includeVideos: props.includeVideos ?? undefined,
                    includeCaptions: props.includeCaptions ?? undefined,
                });
                setTiles(result);
                setLoading(false);
            }
            catch (e) {
                setError(e?.message || 'Failed to fetch Instagram media');
                setLoading(false);
            }
        })();
    }, [accessToken, userId, props.limit, props.useThumbnails, props.includeVideos, props.includeCaptions]);
    if (loading) {
        return (_jsx("div", { style: { padding: 16 }, children: _jsx("p", { children: "Loading Instagram posts..." }) }));
    }
    if (error) {
        return (_jsxs("div", { style: { padding: 16 }, children: [_jsxs("p", { style: { color: 'tomato' }, children: ["Error: ", error] }), _jsx("p", { style: { fontSize: '0.9em', marginTop: 8 }, children: "Make sure you have a valid Instagram user access token with instagram_basic permissions." })] }));
    }
    if (tiles.length === 0) {
        return (_jsx("div", { style: { padding: 16 }, children: _jsx("p", { children: "No Instagram posts found." }) }));
    }
    return _jsx(Tiles, { cards: tiles, rowCount: props.rowCount });
}
